import { RequestsApi } from '../RequestsApi';

class ChatApi extends RequestsApi {
    async getChatsHistory(page) {
        return await this.request(`/chat?page=${page}`, 'get');
    }

    async initChat() {
        return await this.request('/chat', 'post');
    }

    async initChatWithAdvisor(advisorId) {
        return await this.request(`/chat/advisor/${advisorId}`, 'post');
    }

    async initChatWithAdvisorByActivities(activityId) {
        return await this.request(`/chat/activity/${activityId}`, 'post');
    }

    async getChatMessages(params) {
        const { chatId, page, silentOnNetworkError, share } = params;
        // The header is a FE-only signal — backend ignores it. ResponseService
        // checks for it in the global axios error interceptor and skips the
        // "Something went wrong" toast when the failure is a transient
        // network error (axios `ERR_NETWORK`). Used by the tab-resume
        // background resync in ChatBar so the user isn't blasted with a
        // toast on a fetch they didn't initiate.
        const customHeaders = silentOnNetworkError ? { 'X-FE-Silent-Network-Error': '1' } : {};
        // `share=1` tells the backend this read belongs to a share-link visitor,
        // so it leaves the owner's unread badge and post-up pointer alone.
        const shareParam = share ? '&share=1' : '';
        return await this.request(`/chat/${chatId}/messages?page=${page}${shareParam}`, 'get', {}, customHeaders);
    }

    async connectToChat(chatId) {
        return await this.request(`/chat/${chatId}/connect`, 'post');
    }

    async sendChatMessage(params) {
        const { chatId, content, attachments, targetAdvisorAlias, tarot, horoscope, anonTarotCreditsHeader, collapseParagraph, skipIntro } =
            params;
        const body = { content, attachments };
        // Multi-advisor handoff: when present, server routes the assistant reply
        // through this advisor's prompt instead of the chat's default advisor.
        // Omitted entirely on v1 sends so the request shape is unchanged.
        if (targetAdvisorAlias) {
            body.targetAdvisorAlias = targetAdvisorAlias;
        }
        // Tarot reading context: the picked cards, mode, and original question.
        // Server uses these to inject card details into Ophelia's prompt and
        // to stamp the resulting assistant message with payload.tarot for
        // reconstruction in chat history.
        if (tarot) {
            body.tarot = tarot;
        }
        // Daily horoscope request: server resolves the user's zodiac sign,
        // pulls today's feed entry from cache, and asks Veda to paraphrase
        // it. No additional client state needed.
        if (horoscope) {
            body.horoscope = horoscope;
        }
        // Collapse multi-paragraph AI responses into a single Message
        // (paragraphs joined with `\n\n`) instead of one Message per
        // paragraph. Opt-in so v1 sends keep the existing one-row-per-line
        // behaviour; v2 sets this to true for cleaner one-bubble-per-reply
        // rendering.
        if (collapseParagraph) {
            body.collapseParagraph = true;
        }
        // HORO-10327: anonymous clients pass their tarot-credit window
        // (`{used, resetAt}` JSON) so the backend can gate without
        // persisting any state for non-registered users.
        const headers = anonTarotCreditsHeader ? { 'x-anon-tarot-credits': anonTarotCreditsHeader } : {};
        // HORO-10459: `?skipIntro=1` tells the backend to prepend an in-voice
        // "no greeting, don't ask their name" cue to the AI's view of this
        // message (the persisted message is untouched). Needed whenever the
        // advisor has ALREADY greeted the user outside the thread — the v3 cold
        // start shows the intro client-side, and the chat itself is created with
        // `skipHello=1`, so without this the model sees no advisor turn and
        // opens by introducing itself all over again. Same flag every chat-widget
        // version sets on its first message, for the same reason.
        const query = skipIntro ? '?skipIntro=1' : '';
        return await this.request(`/chat/${chatId}/messages${query}`, 'post', body, headers);
    }

    // Tarot follow-up — fired when the user closes the live reveal overlay.
    // The server generates one Veda-voiced message that builds on the most
    // recent reading; we don't pass any payload because all the context
    // (recent messages, advisor) is reconstructed server-side.
    // V2 landing entry: creates a chat (no intro) + sends the first message.
    // Returns { id, advisor }. Called from ChatBar when chatId is absent.
    async startV2Chat({ advisorAlias, content }) {
        return await this.request('/chat/v2/start', 'post', { advisorAlias, content });
    }

    async requestTarotFollowup(chatId) {
        return await this.request(`/chat/${chatId}/tarot/followup`, 'post');
    }

    // HORO-10360: the advisor's follow-up after the birth chart view closes.
    async requestBirthChartFollowup(chatId) {
        return await this.request(`/chat/${chatId}/birth-chart/followup`, 'post');
    }

    async deleteChat(chatId) {
        return await this.request(`/chat/${chatId}/`, 'delete');
    }

    async editChatName(chatId, name) {
        return await this.request(`/chat/${chatId}`, 'put', { name });
    }
    async getChatInfo(chatId) {
        return await this.request(`/chat/${chatId}`, 'get');
    }
    async loadImage(file) {
        return await this.request(`/chat/image/upload`, 'post', file, { 'Content-Type': 'multipart/form-data' });
    }
}

export default new ChatApi();
